import { Controller } from "@hotwired/stimulus";
import { onNextEventLoopTick } from "../helpers/timing_helpers";

// Connects to data-controller="tabs"
// Expected values:
//   - activeTabPanel: String - The panel name of the initially active tab (e.g., "general")
//   - validTabs: Array - Array of valid tab IDs/panel names (e.g., ["general", "visualizations"])
export default class extends Controller {
  static targets = ["tabGroup"];
  static values = {
    activeTabPanel: String,
    validTabs: Array,
  };

  connect() {
    onNextEventLoopTick(() => this.#initializeActiveTab());
  }

  activeTabPanelValueChanged() {
    this.#setActiveTab(this.activeTabPanelValue);
  }

  validTabsValueChanged() {
    if (!this.hasActiveTabPanelValue) this.#initializeActiveTab();
  }

  // Update URL when tab changes (skip if programmatic to avoid duplicate history entries)
  handleTabShow(event) {
    if (this.isSettingTabProgrammatically || !this.validTabsValue) return;
    const tabId = event.detail.name;
    if (this.validTabsValue.includes(tabId)) this.#updateURL(tabId);
  }

  handlePopState() {
    const tabId = new URLSearchParams(window.location.search).get("tab");
    const panelName = this.validTabsValue?.includes(tabId) ? tabId : this.validTabsValue?.[0];
    if (panelName) this.#setActiveTab(panelName, true);
  }

  #setActiveTab(panelName, programmatic = false) {
    if (!this.hasTabGroupTarget || !panelName) return;
    this.isSettingTabProgrammatically = programmatic;
    this.tabGroupTarget.active = panelName;
    if (programmatic) {
      onNextEventLoopTick(() => (this.isSettingTabProgrammatically = false));
    }
  }

  #updateURL(tabId) {
    if (!tabId) return;
    const url = new URL(window.location);
    url.searchParams.set("tab", tabId);
    window.history.pushState({ tab: tabId }, "", url);
  }

  // Initialize from server value, URL, or default to first tab
  #initializeActiveTab() {
    if (this.hasActiveTabPanelValue) {
      this.#setActiveTab(this.activeTabPanelValue);
      return;
    }

    const tabId = new URLSearchParams(window.location.search).get("tab");
    const validTabs = this.validTabsValue || [];
    const panelName = validTabs.includes(tabId) ? tabId : validTabs[0];

    if (panelName) {
      this.#setActiveTab(panelName, !validTabs.includes(tabId));
      if (tabId && !validTabs.includes(tabId)) {
        this.#updateURL(validTabs[0]);
      }
    }
  }
}
